/**
 * The authority on what a valid workflow definition is: `validateWorkflowDefinition` returns every problem with a path, so the API refuses a broken save and the editor can mark the step at fault.
 * Rules it enforces: the schema version, unique ids, known step kinds and actions, required action inputs, complete branches, bounded loops, nesting and waits, the `whenRunning` choice, a list of values only on the right of `in` and `notIn`, step references that point only at actions guaranteed to have run earlier on the same path,
 * settings and flags a run may read, merge-HTML text (its length, workflow tags in scope, record tags only in fields that declare `recordTags`, no Current User tags), and declared variables: unique names, a known type, references only to declared names, and values of the variable's type (`add` on numbers only).
 * Where a value's type is known before the run (a literal, a Job or trigger field, an action output, a setting, a flag, a variable), it must fit
 * where it is used: `isOn`, `before`, `after` and date formats take dates, number and currency formats take numbers, and a list goes only
 * where the action input or the operator takes one. A value whose type only the run knows (a loop item, an unknown path) is not judged.
 * Loosening a rule lets the runtime meet a definition it cannot interpret, which fails the run instead of the save.
 * A value an entity field holds (`WorkflowEntityFieldRef`) is typed from that field in the workflow entity catalogue when the caller passes
 * its lookup, and from the element's own declared metadata otherwise.
 * The same walk records each step's scope, which `workflowStepScope` serves to the editor's binding picker, so the picker offers exactly the references a save accepts.
 */
import { MetadataEntityFieldEditorType, MetadataEntityFieldType } from '../../metadata/entity/decorators/model/metadata.entity.field.types';
import { FeatureFlags } from '../settings/feature.flags';
import { EXTENSION_SETTINGS, isExtensionSettingKey } from './extension.settings';
import type { JsonSafeMetadata } from '../surface.engine/json.safe.metadata.zod';
import { WORKFLOW_SCHEMA_VERSION, WORKFLOW_TOKEN_FORMATS, WorkflowActionType, WorkflowForEachStep, WorkflowOperator, WorkflowStepType, WorkflowTokenFormat, WorkflowValidationIssue, WorkflowValue, WorkflowValueSource, WorkflowVariableOp, WorkflowWhenRunning } from './workflow.definition';
import { WorkflowFieldRenderer, type WorkflowElementField } from './workflow.element';
import type { WorkflowEntityDescriptorLookup } from './workflow.entity.catalogue';
import { workflowEntityField, workflowEntityFieldMetadata } from './workflow.entity.fields';
import { WORKFLOW_MERGE_ROOTS, workflowMergeTags } from './workflow.merge.html';
import { WORKFLOW_ELEMENTS } from './workflow.elements';
import type { WorkflowStepScope } from './workflow.scope';

/** The bounds every definition must stay within; the runtime cell relies on them for its step budget. */
export const WORKFLOW_LIMITS = {
    maxSteps: 200,
    maxDepth: 8,
    maxLoopItems: 100,
    maxListItems: 100,
    maxTextLength: 16000,
    maxWaitDays: 30,
} as const;

/** The types a workflow variable may hold; the runtime only ever stores scalars in the walk. */
export const WORKFLOW_VARIABLE_TYPES: readonly string[] = [MetadataEntityFieldType.String, MetadataEntityFieldType.Number, MetadataEntityFieldType.Boolean];

/** A scalar's kind, as far as a variable's type cares. */
type ScalarKind = 'string' | 'number' | 'boolean';

const SCALAR_KINDS: Record<string, ScalarKind> = {
    [MetadataEntityFieldType.String]: 'string',
    [MetadataEntityFieldType.Number]: 'number',
    [MetadataEntityFieldType.Boolean]: 'boolean',
};
/** What a value is known to hold before the run: a scalar kind, a date (stored as ISO text) or a list. */
type ValueKind = ScalarKind | 'date' | 'list';

const NUMBER_TYPES = new Set<string>([MetadataEntityFieldType.Number, MetadataEntityFieldType.Currency, MetadataEntityFieldType.Percentage]);
const DATE_TYPES = new Set<string>([MetadataEntityFieldType.Date, MetadataEntityFieldType.DateTime, MetadataEntityFieldType.Time]);
const BOOLEAN_TYPES = new Set<string>([MetadataEntityFieldType.Boolean, MetadataEntityFieldType.Flag]);
const TEXT_TYPES = new Set<string>([MetadataEntityFieldType.String, MetadataEntityFieldType.Enum, MetadataEntityFieldType.Lookup]);

/**
 * The trigger paths that hold something other than text. Every other `job.<path>` reads text or a record, because these are all
 * the date, number and list fields of `AgentJobContext` and `ExtensionRunTrigger`; a new non-text field there must be added here,
 * or a date format or date comparison on it is refused.
 */
const TRIGGER_KINDS: Record<string, ValueKind> = {
    'job.startDate': 'date',
    'job.dueDate': 'date',
    'job.completedDate': 'date',
    'job.labels': 'list',
    'trigger.events': 'list',
    'trigger.changedFields': 'list',
    'trigger.coalescedCount': 'number',
    'trigger.firstAt': 'date',
    'trigger.lastAt': 'date',
    'run.startedAt': 'date',
    'now': 'date',
    'workflow.name': 'string',
};

/** The operators that compare calendar days; both sides must be dates. */
const DAY_OPERATORS: readonly string[] = [WorkflowOperator.IsOn, WorkflowOperator.Before, WorkflowOperator.After];
/** The operators whose left side may be a list: membership and emptiness. */
const LIST_LEFT_OPERATORS: readonly string[] = [WorkflowOperator.Contains, WorkflowOperator.NotContains, WorkflowOperator.In, WorkflowOperator.NotIn, WorkflowOperator.IsSet, WorkflowOperator.IsNotSet];
const DATE_FORMATS: readonly string[] = ['date', 'dateNumeric', 'dateLong', 'dateTime', 'time'];
const NUMBER_FORMATS: readonly string[] = ['number', 'currency'];
// The keys a picked record literal may hold (`WorkflowRecordLiteral`); anything else means it was not saved by the editor.
const RECORD_LITERAL_KEYS: readonly string[] = ['_id', 'name', 'color', 'systemStatus'];
const ISO_DATE = /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})?)?$/;

const KIND_NAMES: Record<ScalarKind, string> = {
    string: 'text',
    number: 'a number',
    boolean: 'true/false',
};
const INPUT_PREFIX = 'input.';
const VARIABLE_PATTERN = /^[a-z][A-Za-z0-9_]{0,63}$/;
const ID_PATTERN = /^[a-z][A-Za-z0-9_-]{0,63}$/;
const PATH_PATTERN = /^[A-Za-z0-9_]+(\.[A-Za-z0-9_]+)*$/;
const TRIGGER_ROOTS = ['job', 'trigger'];
const BUILTIN_TRIGGER_PATHS = new Set(['workflow.name', 'run.startedAt', 'now']);
/** Markup a rich text body must never hold: a script or style element, an `on…=` event attribute, a `javascript:` URL. */
const UNSAFE_MARKUP = /<\s*(script|style)\b|<[^>]*\son[a-z]+\s*=|<[^>]*=\s*["']?\s*javascript\s*:/i;
const ANY_TAG = /<[^>]*>/g;
const PLAIN_MULTILINE_TAG = /^<br\s*\/?>$/i;
/** The merge source of the signed-in user; a run is not signed in as anyone, so its tags would render the API caller. */
const CURRENT_USER_ROOT = 'currentUser';
const WAIT_DURATION = /^(\d+) (minute|minutes|hour|hours|day|days)$/;
const MINUTES_PER_UNIT: Record<string, number> = {
    minute: 1,
    minutes: 1,
    hour: 60,
    hours: 60,
    day: 1440,
    days: 1440,
};

/** Collects issues, and each step's scope, while walking one definition. */
class Walk {
    public readonly issues: WorkflowValidationIssue[] = [];
    public readonly ids = new Set<string>();
    /** The scope each step's own values are checked in, keyed by step id; a snapshot, since the walk's action set keeps growing. */
    public readonly scopes = new Map<string, WorkflowStepScope>();
    /** Each declared variable's scalar kind, by name. */
    public readonly variables = new Map<string, ScalarKind>();
    /** Each action step's action, by step id, so a reference to its output knows the output's type. */
    public readonly actions = new Map<string, WorkflowActionType>();
    public steps = 0;

    /** @param entities The workflow entity catalogue, when the caller has it; values entity fields hold are typed from it. */
    public constructor(public readonly entities?: WorkflowEntityDescriptorLookup) { }

    public issue(path: string, message: string): void {

        this.issues.push({ path, message });
    }
}

/**
 * Every problem with `value` as a workflow definition, in document order; empty when it is valid. `entities`, the workflow entity
 * catalogue, types the values entity fields hold; without it they take the element's declared metadata.
 * Called by the API's definition policy before any save and by specs over the checked-in fixtures; never throws for bad input.
 */
export function validateWorkflowDefinition(value: unknown, entities?: WorkflowEntityDescriptorLookup): WorkflowValidationIssue[] {

    return walkDefinition(value, entities).issues;
}

/**
 * What step `stepId` may reference: the actions guaranteed to have run on every path to it and the innermost For each around it.
 * A For each's own `items` sit outside its loop. Null when the definition has no such step. Called by the editor's `workflowScopeAt`.
 */
export function workflowStepScope(value: unknown, stepId: string): WorkflowStepScope | null {

    return walkDefinition(value).scopes.get(stepId) ?? null;
}

function walkDefinition(value: unknown, entities?: WorkflowEntityDescriptorLookup): Walk {

    const walk = new Walk(entities);
    if (!isRecord(value)) {
        walk.issue('', 'A workflow definition is required');
        return walk;
    }
    if (value.schemaVersion !== WORKFLOW_SCHEMA_VERSION) {
        walk.issue('schemaVersion', `Unsupported workflow schema version; expected ${WORKFLOW_SCHEMA_VERSION}`);
    }
    if (!Array.isArray(value.steps) || !value.steps.length) {
        walk.issue('steps', 'A workflow needs at least one step');
        return walk;
    }
    if (value.whenRunning !== undefined && !Object.values(WorkflowWhenRunning).includes(value.whenRunning as WorkflowWhenRunning)) {
        walk.issue('whenRunning', 'When already running, a workflow either queues or replaces');
    }
    if (value.layout !== undefined && !isRecord(value.layout)) {
        walk.issue('layout', 'Layout must map step ids to positions');
    }
    variables(walk, value.variables);
    sequence(walk, value.steps, 'steps', { actions: new Set(), loop: null }, 1);
    if (walk.steps > WORKFLOW_LIMITS.maxSteps) {
        walk.issue('steps', `A workflow may have at most ${WORKFLOW_LIMITS.maxSteps} steps`);
    }
    return walk;
}

/** Validates an ordered list and returns the actions guaranteed to have run after it. */
function sequence(walk: Walk, steps: unknown, path: string, scope: WorkflowStepScope, depth: number): Set<string> {

    const actions = new Set(scope.actions);
    if (!Array.isArray(steps)) {
        walk.issue(path, 'Steps must be a list');
        return actions;
    }
    if (depth > WORKFLOW_LIMITS.maxDepth) {
        walk.issue(path, `Steps may nest at most ${WORKFLOW_LIMITS.maxDepth} levels deep`);
        return actions;
    }
    steps.forEach((step, index) => {

        const ran = stepOf(walk, step, `${path}[${index}]`, { actions, loop: scope.loop }, depth);
        ran.forEach(id => actions.add(id));
    });
    return actions;
}

/** Validates one step and returns the actions it guarantees ran (its own id for an action, the intersection of branches for a branch). */
function stepOf(walk: Walk, step: unknown, path: string, scope: WorkflowStepScope, depth: number): Set<string> {

    walk.steps++;
    if (!isRecord(step)) {
        walk.issue(path, 'A step must be an object');
        return new Set();
    }
    claimId(walk, step.id, `${path}.id`);
    if (typeof step.id === 'string') {
        walk.scopes.set(step.id, { actions: new Set(scope.actions), loop: scope.loop });
    }
    switch (step.type) {
        case WorkflowStepType.Action:
            actionStep(walk, step, path, scope);
            return new Set(typeof step.id === 'string' ? [step.id] : []);
        case WorkflowStepType.If:
            condition(walk, step.condition, `${path}.condition`, scope, 1);
            return intersection([
                sequence(walk, step.then, `${path}.then`, scope, depth + 1),
                sequence(walk, step.else, `${path}.else`, scope, depth + 1),
            ]);
        case WorkflowStepType.Switch:
            return switchStep(walk, step, path, scope, depth);
        case WorkflowStepType.ForEach:
            forEachStep(walk, step, path, scope, depth);
            // A loop may run zero times, so nothing inside it is guaranteed afterwards.
            return new Set();
        case WorkflowStepType.Stop:
            if (step.reason !== undefined) {
                if (isRecord(step.reason) && step.reason.source === WorkflowValueSource.Text) {
                    valueOf(walk, step.reason, `${path}.reason`, scope);
                    textMarkup(walk, step.reason, `${path}.reason`, 'line');
                } else {
                    walk.issue(`${path}.reason`, 'A stop reason must be text');
                }
            }
            return new Set();
        case WorkflowStepType.Wait:
            waitDuration(walk, step.duration, `${path}.duration`);
            return new Set();
        case WorkflowStepType.SetVariable:
            setVariableStep(walk, step, path, scope);
            return new Set();
        default:
            walk.issue(`${path}.type`, 'Unknown step type');
            return new Set();
    }
}

/** Declares each variable: a unique name, a type from `WORKFLOW_VARIABLE_TYPES` and an initial value of that type. */
function variables(walk: Walk, value: unknown): void {

    if (value === undefined) {
        return;
    }
    if (!Array.isArray(value)) {
        walk.issue('variables', 'Variables must be a list');
        return;
    }
    value.forEach((item, index) => {

        const path = `variables[${index}]`;
        if (!isRecord(item)) {
            walk.issue(path, 'A variable must be an object');
            return;
        }
        const name = item.name;
        if (typeof name !== 'string' || !VARIABLE_PATTERN.test(name)) {
            walk.issue(`${path}.name`, 'A variable name starts with a lowercase letter and uses letters, digits or _ (64 at most)');
            return;
        }
        if (walk.variables.has(name)) {
            walk.issue(`${path}.name`, `The variable "${name}" is declared more than once`);
            return;
        }
        const kind = isRecord(item.metadata) ? SCALAR_KINDS[String(item.metadata.type)] : undefined;
        if (!kind) {
            walk.issue(`${path}.metadata.type`, 'A variable holds text, a number or true/false');
            return;
        }
        walk.variables.set(name, kind);
        if (item.initial !== undefined && item.initial !== null && typeof item.initial !== kind) {
            walk.issue(`${path}.initial`, `This variable holds ${KIND_NAMES[kind]}`);
        }
    });
}

/** A declared variable, a known operation, and a value of the variable's kind; `add` takes a number variable and a number. */
function setVariableStep(walk: Walk, step: Record<string, unknown>, path: string, scope: WorkflowStepScope): void {

    const kind = typeof step.variable === 'string' ? walk.variables.get(step.variable) : undefined;
    if (!kind) {
        walk.issue(`${path}.variable`, 'Choose a declared variable');
    }
    const knownOp = Object.values(WorkflowVariableOp).includes(step.op as WorkflowVariableOp);
    if (!knownOp) {
        walk.issue(`${path}.op`, 'A variable is set or added to');
    }
    valueOf(walk, step.value, `${path}.value`, scope);
    if (!kind || !knownOp) {
        return;
    }
    if (step.op === WorkflowVariableOp.Add && kind !== 'number') {
        walk.issue(`${path}.op`, 'Only a number variable can be added to');
        return;
    }
    const have = isRecord(step.value) ? kindOf(walk, step.value as unknown as WorkflowValue) : undefined;
    if (have === 'list') {
        walk.issue(`${path}.value`, 'A variable holds a single value, not a list');
        return;
    }
    // A variable stores a date as its ISO text.
    const scalar = have === 'date' ? 'string' : have;
    if (scalar && scalar !== kind) {
        walk.issue(`${path}.value`, `This variable holds ${KIND_NAMES[kind]}`);
    }
}

/**
 * What `value` is known to hold before the run, or undefined when only the run can tell (a loop item, a path outside the known
 * fields, an empty literal). Composed text always renders as text.
 */
function kindOf(walk: Walk, value: WorkflowValue): ValueKind | undefined {

    switch (value.source) {
        case WorkflowValueSource.Literal:
            return literalKind(value.value);
        case WorkflowValueSource.List:
            return 'list';
        case WorkflowValueSource.Now:
            return 'date';
        case WorkflowValueSource.Text:
            return 'string';
        case WorkflowValueSource.Flag:
            return 'boolean';
        case WorkflowValueSource.Variable:
            return walk.variables.get(value.name);
        case WorkflowValueSource.Trigger:
            return triggerKind(value.path);
        case WorkflowValueSource.Step: {
            const action = walk.actions.get(value.stepId);
            const output = action ? WORKFLOW_ELEMENTS[action].outputs.find(item => item.path === (value.path ?? '')) : undefined;
            const field = output?.entityField && walk.entities ? workflowEntityField(walk.entities, output.entityField) : undefined;
            return metadataKind(field ? workflowEntityFieldMetadata(field) : output?.metadata);
        }
        case WorkflowValueSource.Setting: {
            const setting = isExtensionSettingKey(value.key) ? EXTENSION_SETTINGS[value.key] : undefined;
            const metadata = value.path
                ? setting?.fields.find(field => field.path === value.path)?.metadata
                : setting?.metadata;
            return metadataKind(metadata);
        }
        default:
            return undefined;
    }
}

function literalKind(value: unknown): ValueKind | undefined {

    if (Array.isArray(value)) {
        return 'list';
    }
    if (value === null || value === undefined) {
        return undefined;
    }
    if (typeof value === 'string' && ISO_DATE.test(value)) {
        return 'date';
    }
    // A picked record has no scalar kind: it compares by id with whatever the left side reads.
    if (typeof value === 'object') {
        return undefined;
    }
    return typeof value as ScalarKind;
}

function triggerKind(path: unknown): ValueKind | undefined {

    if (typeof path !== 'string') {
        return undefined;
    }
    if (TRIGGER_KINDS[path]) {
        return TRIGGER_KINDS[path];
    }
    return path.startsWith('job.')
        ? 'string'
        : undefined;
}

function metadataKind(metadata: JsonSafeMetadata | undefined): ValueKind | undefined {

    if (!metadata) {
        return undefined;
    }
    if (metadata.arrayOf || Array.isArray(metadata.type)) {
        return 'list';
    }
    const type = String(metadata.type);
    if (DATE_TYPES.has(type)) {
        return 'date';
    }
    if (NUMBER_TYPES.has(type)) {
        return 'number';
    }
    if (BOOLEAN_TYPES.has(type)) {
        return 'boolean';
    }
    return TEXT_TYPES.has(type)
        ? 'string'
        : undefined;
}

function actionStep(walk: Walk, step: Record<string, unknown>, path: string, scope: WorkflowStepScope): void {

    // Flow-control elements share the registry, so only an action type may name an action.
    const element = Object.values(WorkflowActionType).includes(step.action as WorkflowActionType)
        ? WORKFLOW_ELEMENTS[step.action as WorkflowActionType]
        : undefined;
    if (!element) {
        walk.issue(`${path}.action`, 'Unknown action');
        return;
    }
    if (typeof step.id === 'string') {
        walk.actions.set(step.id, step.action as WorkflowActionType);
    }
    if (!isRecord(step.input)) {
        walk.issue(`${path}.input`, 'Action input must be an object');
        return;
    }
    // An action element binds each input at `input.<name>`.
    const inputs = element.fields.map(field => ({ name: field.bind.slice(INPUT_PREFIX.length), required: field.required === true, list: takesList(field), field }));
    const known = new Set(inputs.map(input => input.name));
    Object.keys(step.input)
        .filter(name => !known.has(name))
        .forEach(name => walk.issue(`${path}.input.${name}`, 'This action has no such input'));
    for (const input of inputs) {
        const value = step.input[input.name];
        if (value === undefined) {
            if (input.required) {
                walk.issue(`${path}.input.${input.name}`, 'This input is required');
            }
            continue;
        }
        inputValue(walk, value, `${path}.input.${input.name}`, scope, input.list, input.field.recordTags === true);
        if (input.field.renderer === WorkflowFieldRenderer.MergeText) {
            textMarkup(walk, value, `${path}.input.${input.name}`, textRoleOf(input.field));
        }
    }
}

/** Whether an element field holds a list, so a list of values or a binding to a list may fill it. */
function takesList(field: WorkflowElementField): boolean {

    return !!field.metadata?.arrayOf || Array.isArray(field.metadata?.type);
}

/**
 * One action input: literal values or a list value where the field takes a list, a single value everywhere else; record tags only
 * where `recordTags` says WorkBuddy merges the input against the run's record.
 */
function inputValue(walk: Walk, value: unknown, path: string, scope: WorkflowStepScope, list: boolean, recordTags: boolean): void {

    if (list && isRecord(value) && value.source === WorkflowValueSource.Literal && Array.isArray(value.value)) {
        literalList(walk, value.value, `${path}.value`);
        return;
    }
    valueOf(walk, value, path, scope, recordTags);
    if (!list && operandKind(walk, value) === 'list') {
        walk.issue(path, 'This input takes a single value, not a list');
    }
}

function switchStep(walk: Walk, step: Record<string, unknown>, path: string, scope: WorkflowStepScope, depth: number): Set<string> {

    valueOf(walk, step.value, `${path}.value`, scope);
    if (operandKind(walk, step.value) === 'list') {
        walk.issue(`${path}.value`, 'A switch matches a single value, not a list');
    }
    if (!Array.isArray(step.cases) || !step.cases.length) {
        walk.issue(`${path}.cases`, 'A switch needs at least one case');
        return new Set();
    }
    const seenEquals = new Set<string>();
    const branches = step.cases.map((item, index) => {

        const casePath = `${path}.cases[${index}]`;
        if (!isRecord(item)) {
            walk.issue(casePath, 'A case must be an object');
            return new Set<string>();
        }
        claimId(walk, item.id, `${casePath}.id`);
        if (item.label !== undefined && (typeof item.label !== 'string' || item.label.length > WORKFLOW_LIMITS.maxTextLength)) {
            walk.issue(`${casePath}.label`, `A case label must be text of at most ${WORKFLOW_LIMITS.maxTextLength} characters`);
        }
        const candidates = Array.isArray(item.equals) ? item.equals : [item.equals];
        if (!candidates.length || candidates.length > WORKFLOW_LIMITS.maxListItems || !candidates.every(isLiteral)) {
            walk.issue(`${casePath}.equals`, 'A case matches a text, number, true/false or empty value');
        } else {
            for (const candidate of candidates) {
                const key = `${typeof candidate}:${String(candidate)}`;
                if (seenEquals.has(key)) {
                    walk.issue(`${casePath}.equals`, `The case value "${String(candidate)}" is already handled by an earlier case`);
                }
                seenEquals.add(key);
            }
        }
        return sequence(walk, item.steps, `${casePath}.steps`, scope, depth + 1);
    });
    branches.push(sequence(walk, step.default, `${path}.default`, scope, depth + 1));
    return intersection(branches);
}

function forEachStep(walk: Walk, step: Record<string, unknown>, path: string, scope: WorkflowStepScope, depth: number): void {

    valueOf(walk, step.items, `${path}.items`, scope);
    const maxItems = step.maxItems;
    if (typeof maxItems !== 'number' || !Number.isSafeInteger(maxItems) || maxItems < 1 || maxItems > WORKFLOW_LIMITS.maxLoopItems) {
        walk.issue(`${path}.maxItems`, `A loop must cap its items between 1 and ${WORKFLOW_LIMITS.maxLoopItems}`);
    }
    sequence(walk, step.steps, `${path}.steps`, { actions: scope.actions, loop: step as unknown as WorkflowForEachStep }, depth + 1);
}

/** A wait lasts `<count> <unit>`, from 1 minute to `WORKFLOW_LIMITS.maxWaitDays` days; a wait inside a loop waits on every pass and counts as a step like any other. */
function waitDuration(walk: Walk, duration: unknown, path: string): void {

    const match = typeof duration === 'string'
        ? WAIT_DURATION.exec(duration)
        : null;
    if (!match) {
        walk.issue(path, 'A wait lasts a number of minutes, hours or days, such as "2 days"');
        return;
    }
    const minutes = Number(match[1]) * MINUTES_PER_UNIT[match[2]];
    if (minutes < 1 || minutes > WORKFLOW_LIMITS.maxWaitDays * MINUTES_PER_UNIT.days) {
        walk.issue(path, `A wait lasts from 1 minute to ${WORKFLOW_LIMITS.maxWaitDays} days`);
    }
}

function condition(walk: Walk, value: unknown, path: string, scope: WorkflowStepScope, depth: number): void {

    if (!isRecord(value)) {
        walk.issue(path, 'A condition must be an object');
        return;
    }
    if (depth > WORKFLOW_LIMITS.maxDepth) {
        walk.issue(path, `Conditions may nest at most ${WORKFLOW_LIMITS.maxDepth} levels deep`);
        return;
    }
    if ('all' in value || 'any' in value) {
        const key = 'all' in value ? 'all' : 'any';
        const items = value[key];
        if (!Array.isArray(items) || !items.length) {
            walk.issue(`${path}.${key}`, 'A condition group needs at least one condition');
            return;
        }
        items.forEach((item, index) => condition(walk, item, `${path}.${key}[${index}]`, scope, depth + 1));
        return;
    }
    if ('not' in value) {
        condition(walk, value.not, `${path}.not`, scope, depth + 1);
        return;
    }
    comparison(walk, value, path, scope);
}

function comparison(walk: Walk, value: Record<string, unknown>, path: string, scope: WorkflowStepScope): void {

    if (!Object.values(WorkflowOperator).includes(value.operator as WorkflowOperator)) {
        walk.issue(`${path}.operator`, 'Unknown comparison');
        return;
    }
    const operator = value.operator as WorkflowOperator;
    valueOf(walk, value.left, `${path}.left`, scope);
    const unary = operator === WorkflowOperator.IsSet || operator === WorkflowOperator.IsNotSet;
    if (unary && value.right !== undefined) {
        walk.issue(`${path}.right`, 'This comparison takes no second value');
        return;
    }
    const left = operandKind(walk, value.left);
    if (left === 'list' && !LIST_LEFT_OPERATORS.includes(operator)) {
        walk.issue(`${path}.left`, 'A list is compared with contains or is any of, not this comparison');
    }
    if (DAY_OPERATORS.includes(operator) && left && left !== 'date') {
        walk.issue(`${path}.left`, 'Is on, before and after compare dates');
    }
    if (operator === WorkflowOperator.In || operator === WorkflowOperator.NotIn) {
        listOperand(walk, value.right, `${path}.right`, scope);
        return;
    }
    if (unary) {
        return;
    }
    valueOf(walk, value.right, `${path}.right`, scope);
    const right = operandKind(walk, value.right);
    if (right === 'list') {
        walk.issue(`${path}.right`, 'Compare with a single value; use is any of for a list');
    }
    if (DAY_OPERATORS.includes(operator) && right && right !== 'list' && right !== 'date') {
        walk.issue(`${path}.right`, 'Is on, before and after compare dates');
    }
}

/** What a value placed somewhere holds; undefined for a literal list, which `valueOf` already refuses where none is taken. */
function operandKind(walk: Walk, value: unknown): ValueKind | undefined {

    if (!isRecord(value)) {
        return undefined;
    }
    if (value.source === WorkflowValueSource.Literal && Array.isArray(value.value)) {
        return undefined;
    }
    return kindOf(walk, value as unknown as WorkflowValue);
}

/** The right side of `in` and `notIn`: one or more literal values, a non-empty `list` value, or any other binding which the run reads as a list. */
function listOperand(walk: Walk, value: unknown, path: string, scope: WorkflowStepScope): void {

    if (!isRecord(value)) {
        valueOf(walk, value, path, scope);
        return;
    }
    if (value.source === WorkflowValueSource.Literal) {
        literalList(walk, value.value, `${path}.value`);
        return;
    }
    if (value.source === WorkflowValueSource.List) {
        if (!Array.isArray(value.items) || !value.items.length) {
            walk.issue(`${path}.items`, 'Choose one or more values');
            return;
        }
        listValue(walk, value.items, path, scope);
        return;
    }
    valueOf(walk, value, path, scope);
}

/** One or more literal values, at most `WORKFLOW_LIMITS.maxListItems`. */
function literalList(walk: Walk, value: unknown, path: string): void {

    if (!Array.isArray(value) || !value.length || value.length > WORKFLOW_LIMITS.maxListItems) {
        walk.issue(path, 'Choose one or more values');
        return;
    }
    if (!value.every(isLiteral)) {
        walk.issue(path, 'Each value is text, a number, true/false, a picked record or empty');
    }
}

/** One value of any source; text may hold record tags only when `recordTags` (the field's `recordTags`). */
function valueOf(walk: Walk, value: unknown, path: string, scope: WorkflowStepScope, recordTags = false): void {

    if (!isRecord(value)) {
        walk.issue(path, 'A value must say where it comes from');
        return;
    }
    const binding = value as unknown as WorkflowValue;
    switch (binding.source) {
        case WorkflowValueSource.Literal:
            if (!isLiteral(binding.value)) {
                walk.issue(`${path}.value`, 'A literal is text, a number, true/false, a picked record or empty');
            }
            return;
        case WorkflowValueSource.List:
            listValue(walk, binding.items, path, scope);
            return;
        case WorkflowValueSource.Now:
            formatOptions(walk, value, path, kindOf(walk, binding));
            return;
        case WorkflowValueSource.Trigger:
            triggerPath(walk, binding.path, `${path}.path`);
            formatOptions(walk, value, path, kindOf(walk, binding));
            return;
        case WorkflowValueSource.Step:
            stepReference(walk, binding.stepId, binding.path, path, scope);
            formatOptions(walk, value, path, kindOf(walk, binding));
            return;
        case WorkflowValueSource.Item:
            if (!scope.loop) {
                walk.issue(path, 'A loop item is only available inside a loop');
            }
            optionalPath(walk, binding.path, `${path}.path`);
            formatOptions(walk, value, path, kindOf(walk, binding));
            return;
        case WorkflowValueSource.Text:
            mergeHtml(walk, value.html, `${path}.html`, scope, recordTags);
            return;
        case WorkflowValueSource.Setting:
            settingReference(walk, binding.key, binding.path, path);
            formatOptions(walk, value, path, kindOf(walk, binding));
            return;
        case WorkflowValueSource.Flag:
            flagReference(walk, binding.flag, path);
            formatOptions(walk, value, path, kindOf(walk, binding));
            return;
        case WorkflowValueSource.Variable:
            variableReference(walk, binding.name, path);
            formatOptions(walk, value, path, kindOf(walk, binding));
            return;
        default:
            walk.issue(`${path}.source`, 'Unknown value source');
    }
}

function listValue(walk: Walk, items: unknown, path: string, scope: WorkflowStepScope): void {

    if (!Array.isArray(items) || items.length > WORKFLOW_LIMITS.maxListItems) {
        walk.issue(`${path}.items`, `A list value must hold up to ${WORKFLOW_LIMITS.maxListItems} items`);
        return;
    }
    items.forEach((item, index) => {

        const itemPath = `${path}.items[${index}]`;
        if (isRecord(item) && item.source === WorkflowValueSource.List) {
            walk.issue(itemPath, 'A list cannot contain another list');
            return;
        }
        valueOf(walk, item, itemPath, scope);
    });
}

/** What markup a merge-text field's HTML may hold: `rich` formatting (a RichText editor), or plain text on one `line` or `multiline` (`<br>`). */
type TextRole = 'rich' | 'line' | 'multiline';

/** The role an element field's editor gives its text: RichText is rich, TextArea plain on several lines, anything else one plain line. */
function textRoleOf(field: WorkflowElementField): TextRole {

    switch (field.metadata?.editor) {
        case MetadataEntityFieldEditorType.RichText:
            return 'rich';
        case MetadataEntityFieldEditorType.TextArea:
            return 'multiline';
        default:
            return 'line';
    }
}

/**
 * The markup a text value's HTML may hold where it is used. Plain text (a subject, an SMS, a title, a stop reason) is sent as text,
 * so it holds text and merge spans only (and `<br>` on several lines); rich text (an email or note body) is HTML a customer opens,
 * so it never holds a script, a style, an event attribute or a `javascript:` link.
 */
function textMarkup(walk: Walk, value: unknown, path: string, role: TextRole): void {

    if (!isRecord(value) || value.source !== WorkflowValueSource.Text || typeof value.html !== 'string') {
        return;
    }
    const html = value.html;
    if (role === 'rich') {
        if (UNSAFE_MARKUP.test(html)) {
            walk.issue(`${path}.html`, "Scripts, styles and event handlers aren't allowed here");
        }
        return;
    }
    let rest = '';
    let lastIndex = 0;
    for (const tag of workflowMergeTags(html)) {
        rest += html.slice(lastIndex, tag.start);
        lastIndex = tag.end;
    }
    rest += html.slice(lastIndex);
    const allowed = role === 'multiline' ? PLAIN_MULTILINE_TAG : null;
    const formatted = [...rest.matchAll(ANY_TAG)].some(match => !allowed || !allowed.test(match[0]));
    if (formatted) {
        walk.issue(`${path}.html`, "Formatting isn't allowed here");
    }
}

/**
 * Merge HTML: text of at most `WORKFLOW_LIMITS.maxTextLength` characters. A workflow tag must read something this step can reach,
 * with display options that fit it. A record tag is allowed only where `recordTags` (the field declares it); there it must be a
 * dotted merge name, and never `currentUser`, which a run has no user for. Which record tags exist is the server's merge catalogue
 * to say; its preview reports an unknown one.
 */
function mergeHtml(walk: Walk, html: unknown, path: string, scope: WorkflowStepScope, recordTags: boolean): void {

    if (typeof html !== 'string') {
        walk.issue(path, 'Enter text');
        return;
    }
    if (html.length > WORKFLOW_LIMITS.maxTextLength) {
        walk.issue(path, `Text must be at most ${WORKFLOW_LIMITS.maxTextLength} characters`);
        return;
    }
    for (const tag of workflowMergeTags(html)) {
        const root = tag.name.split('.')[0];
        if (tag.value) {
            valueOf(walk, tag.value, path, scope);
            formatOptions(walk, tag as unknown as Record<string, unknown>, path, kindOf(walk, tag.value));
        } else if (WORKFLOW_MERGE_ROOTS.includes(root)) {
            walk.issue(path, `Unknown workflow field ${tag.name}`);
        } else if (!recordTags) {
            walk.issue(path, "Record fields can't be used here; use a workflow field");
        } else if (!PATH_PATTERN.test(tag.name)) {
            walk.issue(path, `A merge field is named by dot-separated names, not "${tag.name}"`);
        } else if (root === CURRENT_USER_ROOT) {
            walk.issue(path, 'Current User fields have no value in a workflow run');
        }
    }
}

/** A token's display options; a date format needs a date and a number or currency format a number, when the value's type is known. */
function formatOptions(walk: Walk, holder: Record<string, unknown>, path: string, kind: ValueKind | undefined): void {

    if (holder.format !== undefined && !WORKFLOW_TOKEN_FORMATS.includes(holder.format as WorkflowTokenFormat)) {
        walk.issue(`${path}.format`, 'Unknown token format');
    } else if (DATE_FORMATS.includes(holder.format as string) && kind && kind !== 'date') {
        walk.issue(`${path}.format`, 'A date format needs a date');
    } else if (NUMBER_FORMATS.includes(holder.format as string) && kind && kind !== 'number') {
        walk.issue(`${path}.format`, 'A number or currency format needs a number');
    }
    if (holder.decimals !== undefined && (typeof holder.decimals !== 'number' || !Number.isSafeInteger(holder.decimals) || holder.decimals < 0 || holder.decimals > 6)) {
        walk.issue(`${path}.decimals`, 'Decimals must be a whole number from 0 to 6');
    }
    if (holder.ifEmpty !== undefined && (typeof holder.ifEmpty !== 'string' || holder.ifEmpty.length > WORKFLOW_LIMITS.maxTextLength)) {
        walk.issue(`${path}.ifEmpty`, `Fallback text must be at most ${WORKFLOW_LIMITS.maxTextLength} characters`);
    }
    if (holder.join !== undefined && (typeof holder.join !== 'string' || holder.join.length > WORKFLOW_LIMITS.maxTextLength)) {
        walk.issue(`${path}.join`, `List separator must be at most ${WORKFLOW_LIMITS.maxTextLength} characters`);
    }
}

function triggerPath(walk: Walk, path: unknown, at: string): void {

    if (typeof path !== 'string' || (!BUILTIN_TRIGGER_PATHS.has(path) && (!PATH_PATTERN.test(path) || !TRIGGER_ROOTS.includes(path.split('.')[0])))) {
        walk.issue(at, 'A trigger value must name a path under job or trigger');
    }
}

function stepReference(walk: Walk, stepId: unknown, path: unknown, at: string, scope: WorkflowStepScope): void {

    if (typeof stepId !== 'string' || !scope.actions.has(stepId)) {
        walk.issue(at, 'This value refers to an action that has not run on every path to this step');
    }
    optionalPath(walk, path, at);
}

/** A setting a run may read, and when a path is given, one of its declared fields. */
function settingReference(walk: Walk, key: unknown, path: unknown, at: string): void {

    if (!isExtensionSettingKey(key)) {
        walk.issue(at, 'This value reads a setting a workflow cannot read');
        return;
    }
    if (path !== undefined && path !== '' && !EXTENSION_SETTINGS[key].fields.some(field => field.path === path)) {
        walk.issue(at, `The setting ${key} has no field ${String(path)}`);
    }
}

function flagReference(walk: Walk, flag: unknown, at: string): void {

    if (!Object.values(FeatureFlags).includes(flag as FeatureFlags)) {
        walk.issue(at, 'This value reads an unknown feature flag');
    }
}

function variableReference(walk: Walk, name: unknown, at: string): void {

    if (typeof name !== 'string' || !walk.variables.has(name)) {
        walk.issue(at, 'This value reads a variable that is not declared');
    }
}

function optionalPath(walk: Walk, path: unknown, at: string): void {

    if (path !== undefined && path !== '' && (typeof path !== 'string' || !PATH_PATTERN.test(path))) {
        walk.issue(at, 'A path is dot-separated names');
    }
}

function claimId(walk: Walk, id: unknown, path: string): void {

    if (typeof id !== 'string' || !ID_PATTERN.test(id)) {
        walk.issue(path, 'An id starts with a lowercase letter and uses letters, digits, - or _ (64 at most)');
        return;
    }
    if (walk.ids.has(id)) {
        walk.issue(path, `The id "${id}" is used more than once`);
    }
    walk.ids.add(id);
}

/** The actions every branch ran; an action in only some branches may not have run after the step. */
function intersection(branches: Set<string>[]): Set<string> {

    const [first, ...rest] = branches;
    return new Set([...(first ?? [])].filter(id => rest.every(branch => branch.has(id))));
}

function isLiteral(value: unknown): boolean {

    return value === null || ['string', 'number', 'boolean'].includes(typeof value) || isRecordLiteral(value);
}

/** Whether `value` is a picked record as the editor saves it (`WorkflowRecordLiteral`): an `_id`, and only text beside it. */
function isRecordLiteral(value: unknown): boolean {

    if (!isRecord(value) || typeof value._id !== 'string' || !value._id) {
        return false;
    }
    return Object.entries(value).every(([key, item]) => RECORD_LITERAL_KEYS.includes(key) && (key === '_id' || item === undefined || typeof item === 'string'));
}

function isRecord(value: unknown): value is Record<string, unknown> {

    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
